import { useDefaultSubscription } from 'common/services/useDefaultSubscription'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useSubscriptions from 'common/useSubscriptions'
import { useGetUserQuery, useLogoutMutation } from 'common/services/useUser'
import { useRouter } from 'next/router'
import cognito from 'common/cognito'
import Link from 'next/link'
import dataRelay from 'data-relay'
import { useFlags } from 'flagsmith/react'
import { useUserData } from 'common/services/useUserData'
import { BillingPeriod, BusinessType } from 'common/types/enum'
import { Project } from 'common/project'
import CompanyAvatar from './CompanyAvatar'
import Button from './base/forms/Button'
import Input from './base/forms/Input'
import useOnClickOutside from 'project/hooks/useOnClickOutside'
import { ChevronDownIcon } from 'project/icons'
import Cropper, { Area } from 'react-easy-crop'
import { useDropzone } from 'react-dropzone'
import ModalDefault, { CloseIcon } from './base/ModalDefault'
import { useUploadCompanyLogoMutation } from 'common/services/useCompanyLogo'
import { getCompanies } from 'common/services/useCompany'
import { getStore } from 'common/store'
import { toast } from './base/Toast'
import Icon from 'project/Icon'
import {
  ACCEPTED_LOGO_TYPES,
  MAX_LOGO_SIZE,
  getCroppedImage,
  isSquareImage,
} from 'common/utils/logoUpload'
import { getPlanConfig } from './ProductIcon'

const CameraIcon = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 20 20'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <circle cx='10' cy='10' r='10' fill='#1D1D1F' />
    <path
      d='M14.2993 8.25928C14.2993 7.87268 13.9857 7.55908 13.5991 7.55908H12.3994C12.2216 7.55908 12.0569 7.46464 11.9673 7.31104L11.272 6.11914H8.72656L8.03125 7.31104C7.94164 7.46464 7.77695 7.55908 7.59912 7.55908H6.39941C6.01281 7.55908 5.69922 7.87268 5.69922 8.25928V13.1792C5.69925 13.5658 6.01283 13.8789 6.39941 13.8789H13.5991C13.9857 13.8789 14.2993 13.5658 14.2993 13.1792V8.25928ZM11.2993 10.4189C11.2992 9.70106 10.7169 9.11914 9.99902 9.11914C9.28121 9.11925 8.69932 9.70113 8.69922 10.4189C8.69922 11.1369 9.28114 11.7191 9.99902 11.7192C10.717 11.7192 11.2993 11.1369 11.2993 10.4189ZM12.2993 10.4189C12.2993 11.6892 11.2693 12.7192 9.99902 12.7192C8.72886 12.7191 7.69922 11.6891 7.69922 10.4189C7.69932 9.14884 8.72892 8.11925 9.99902 8.11914C11.2692 8.11914 12.2992 9.14878 12.2993 10.4189ZM15.2993 13.1792C15.2993 14.1181 14.538 14.8789 13.5991 14.8789H6.39941C5.46055 14.8789 4.69925 14.1181 4.69922 13.1792V8.25928C4.69922 7.32039 5.46053 6.55908 6.39941 6.55908H7.31201L8.00732 5.36719L8.04443 5.3125C8.13837 5.19153 8.28386 5.11914 8.43945 5.11914H11.5591C11.7369 5.11914 11.9016 5.21358 11.9912 5.36719L12.6865 6.55908H13.5991C14.538 6.55908 15.2993 7.32039 15.2993 8.25928V13.1792Z'
      fill='white'
    />
  </svg>
)

type SubscriptionSelectType = {}

const SubscriptionSelect: FC<SubscriptionSelectType> = ({}) => {
  const { setDefaultSubscriptionId, subscriptionDetail } =
    useDefaultSubscription()
  const router = useRouter()
  const subscriptions = useSubscriptions()
  const { data: user } = useGetUserQuery({})
  const { userData } = useUserData()
  const [logout] = useLogoutMutation()
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Logo upload state
  const [uploadLogo, { isLoading: isUploadingLogo }] =
    useUploadCompanyLogoMutation()
  const [cropModalOpen, setCropModalOpen] = useState(false)
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const companyId = subscriptionDetail?.company_id

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels)
    },
    [],
  )

  const saveLogoFile = useCallback(
    async (file: File) => {
      if (!companyId) return
      try {
        await uploadLogo({ company_id: companyId, logo: file }).unwrap()
        await getCompanies(getStore(), {}, { forceRefetch: true })
        toast(null, 'Company logo updated')
      } catch {
        toast('Failed to upload logo', null, 'danger')
      }
    },
    [companyId, uploadLogo],
  )

  const handleCropConfirm = useCallback(async () => {
    if (!cropImageSrc || !croppedAreaPixels) return
    try {
      const croppedFile = await getCroppedImage(cropImageSrc, croppedAreaPixels)
      setCropModalOpen(false)
      setCropImageSrc(null)
      setCrop({ x: 0, y: 0 })
      setZoom(1)
      await saveLogoFile(croppedFile)
    } catch {
      toast('Failed to crop image', null, 'danger')
    }
  }, [cropImageSrc, croppedAreaPixels, saveLogoFile])

  const onLogoDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      if (!file || !companyId) return
      if (file.size > MAX_LOGO_SIZE) {
        toast('File must be less than 5MB', null, 'danger')
        return
      }
      if (file.type === 'image/svg+xml') {
        await saveLogoFile(file)
        return
      }
      const isSquare = await isSquareImage(file)
      if (isSquare) {
        await saveLogoFile(file)
      } else {
        setCropImageSrc(URL.createObjectURL(file))
        setCropModalOpen(true)
      }
    },
    [companyId, saveLogoFile],
  )

  const { getInputProps: getLogoInputProps, open: openFilePicker } =
    useDropzone({
      accept: ACCEPTED_LOGO_TYPES,
      disabled: isUploadingLogo || !companyId,
      maxFiles: 1,
      noClick: true,
      noDrag: true,
      onDrop: onLogoDrop,
      onDropRejected: () => {
        toast('Invalid file type. Use PNG, JPG, WebP, or SVG.', null, 'danger')
      },
    })

  useOnClickOutside(dropdownRef, () => {
    setOpen(false)
    setSearchQuery('')
  })

  const handleSelect = (id: number) => {
    if (router.query.subscriptionId) {
      router.replace('/')
    }
    setDefaultSubscriptionId(id)
    setOpen(false)
    setSearchQuery('')
  }

  useEffect(() => {
    const handleRouteChange = () => {
      setOpen(false)
      setSearchQuery('')
    }
    router.events.on('routeChangeStart', handleRouteChange)
    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [router.events])

  const otherSubscriptions = useMemo(() => {
    if (!subscriptions || !subscriptionDetail) return []
    return subscriptions.filter((s) => s.id !== subscriptionDetail.id)
  }, [subscriptions, subscriptionDetail])

  const filteredSubscriptions = useMemo(() => {
    if (!searchQuery) return otherSubscriptions
    const query = searchQuery.toLowerCase()
    return otherSubscriptions.filter((s) =>
      s.company_name?.toLowerCase().includes(query),
    )
  }, [otherSubscriptions, searchQuery])

  const { start_another_business } = useFlags(['start_another_business'])

  const checkout = () => {
    if (!user) return
    const data = {
      'address': {
        'address_line_1': '',
        'address_line_2': '',
        'city': '',
        'country': '',
        'postal_code': '',
        ...(subscriptionDetail?.address || {}),
        'first_name': userData?.first_name || '',
        'last_name': userData?.last_name || '',
      },
      'billing_address': {
        'address_line_1': '',
        'address_line_2': '',
        'city': '',
        'country': '',
        'postal_code': '',
        ...(subscriptionDetail?.address || {}),
        'first_name': userData?.first_name || '',
        'last_name': userData?.last_name || '',
      },
      'business': {
        'business_type': BusinessType.LIMITED_COMPANY,
        'company_number': '',
        'country_of_corporation': 'GB',
        'description': '',
        'legal_name': '',
        'trading_name': '',
      },
      'customer_email': user?.username || '',
      'first_name': userData?.first_name || '',
      'last_name': userData?.last_name || '',
      'payment_type': '',
      'phone_number': '',
      'product_id': '',
      'reference_id': '',
    }
    const foundSlug = 'Virtual-Office-London'
    const cookieJSON: Record<string, string> = {
      referrer: user.username!,
    }
    if (start_another_business.value) {
      cookieJSON.coupon = `${start_another_business.value}`
    }
    data.product_id = foundSlug
    cookieJSON.bag = JSON.stringify({
      'addons': [],
      'duration': 'month',
      'slug': 'Virtual-Office-London',
    })
    if (Project.dataRelay) {
      try {
        dataRelay.sendEvent(
          {
            company: data.business.legal_name || data.business.trading_name,
            email: data.customer_email,
            event: 'Second business click',
            product: data.product_id,
          },
          { apiKey: Project.dataRelay },
        )
      } catch (e) {}
    }
    cookieJSON.checkout = JSON.stringify(data)
    const cookieUrl = encodeURIComponent(btoa(JSON.stringify(cookieJSON)))
    const utm =
      '&utm_source=customer_portal&utm_medium=internal_cta&utm_campaign=second_business_signup'
    if (foundSlug) {
      window.open(
        `${Project.marketingSiteUrl}/checkout?c=${cookieUrl}${utm}`,
        '_blank',
      )
    } else {
      window.open(`${Project.marketingSiteUrl}?c=${cookieUrl}${utm}`, '_blank')
    }
  }

  const companyLogoUrl = subscriptionDetail?.company?.logo_url
  const firstName = userData?.first_name || ''
  const fullName = [userData?.first_name, userData?.last_name]
    .filter(Boolean)
    .join(' ')

  const getBillingLabel = (
    billingPeriod: BillingPeriod | string | undefined,
  ) => {
    if (billingPeriod === BillingPeriod.ANNUAL) return 'Billed annually'
    if (billingPeriod === BillingPeriod.MONTHLY) return 'Billed monthly'
    return ''
  }

  return (
    <div className='position-relative' ref={dropdownRef}>
      {/* Trigger */}
      <div
        onClick={() => setOpen(!open)}
        className='cursor-pointer d-flex gap-2 align-items-center'
        role='button'
        aria-haspopup='true'
        aria-expanded={open}
        aria-label='Open account menu'
      >
        <CompanyAvatar
          logoUrl={companyLogoUrl}
          companyName={subscriptionDetail?.company_name}
          size={44}
        />
        <div className='d-none d-lg-flex flex-column'>
          <span className='fw-bold' style={{ fontSize: 15 }}>
            {fullName || 'Account'}
          </span>
          <span
            className='text-muted fs-xss'
            style={{ letterSpacing: '-0.36px' }}
          >
            {user?.username}
          </span>
        </div>
        <ChevronDownIcon />
      </div>

      {/* Dropdown */}
      {open && (
        <div
          className='position-fixed bg-white d-flex flex-column overflow-auto account-dropdown shadow-4'
          style={{
            padding: '11px 20px 20px',
            zIndex: 1050,
          }}
        >
          {/* Close button */}
          <CloseIcon
            className='cursor-pointer position-absolute'
            style={{ right: 10, top: 10, zIndex: 1 }}
            onClick={() => setOpen(false)}
          />
          {/* Header */}
          <div className='text-center'>
            <h3 className='fw-bold mb-0'>{subscriptionDetail?.company_name}</h3>
            {subscriptionDetail?.plan_type && (
              <span className='fw-semibold fs-md d-block'>
                {
                  getPlanConfig(
                    subscriptionDetail.plan_type,
                    subscriptionDetail,
                  ).title
                }
                {subscriptionDetail.billing_period
                  ? ` - ${getBillingLabel(subscriptionDetail.billing_period)}`
                  : ''}
              </span>
            )}
          </div>

          {/* Company Logo */}
          <div className='d-flex flex-column align-items-center mt-4'>
            <input {...getLogoInputProps()} />
            <div
              className='position-relative cursor-pointer'
              onClick={(e) => {
                e.stopPropagation()
                openFilePicker()
              }}
            >
              <CompanyAvatar
                logoUrl={companyLogoUrl}
                companyName={subscriptionDetail?.company_name}
                size={70}
              />
              <div
                className='position-absolute'
                style={{ bottom: 0, right: 0 }}
              >
                <CameraIcon />
              </div>
            </div>
            <h1 className='fw-semibold mb-0 mt-1'>
              Hi, {firstName || 'there'}
            </h1>
            <span className='fw-semibold text-muted fs-md'>
              {user?.username}
            </span>
          </div>

          {/* Manage Account */}
          <div className='d-flex justify-content-center mt-2'>
            <Link
              href='/account'
              className='fs-sm'
              onClick={() => setOpen(false)}
            >
              <Button theme='secondary' size='small' style={{ width: 180 }}>
                Manage your Account
              </Button>
            </Link>
          </div>

          {/* Divider */}
          <hr style={{ borderColor: '#6E6E73', margin: '20px 0 16px' }} />

          {/* Switch company */}
          {otherSubscriptions.length > 0 && (
            <div className='mb-3'>
              <span className='fw-semibold fs-xs'>Switch company</span>
              {otherSubscriptions.length > 5 && (
                <Input
                  className='mt-2'
                  placeholder='Search companies...'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              )}
              <div
                className='d-flex flex-column mt-3 overflow-auto'
                style={{ gap: 10, maxHeight: 125 }}
              >
                {filteredSubscriptions.map((subscription) => {
                  const billingLabel = getBillingLabel(
                    subscription.billing_period,
                  )
                  const subDescription = [subscription.name, billingLabel]
                    .filter(Boolean)
                    .join(' - ')

                  return (
                    <div
                      key={subscription.id}
                      className='d-flex align-items-center cursor-pointer rounded-lg px-2 py-1 company-switch-row'
                      style={{ gap: 10 }}
                      onClick={() => handleSelect(subscription.id)}
                      role='button'
                      aria-label={`Switch to ${subscription.company_name}`}
                    >
                      <CompanyAvatar
                        logoUrl={subscription.company?.logo_url}
                        companyName={subscription.company_name}
                        size={24}
                      />
                      <div
                        className='d-flex flex-column flex-grow-1'
                        style={{ lineHeight: 1.18 }}
                      >
                        <div className='d-flex align-items-center'>
                          <span className='fw-bold flex-grow-1'>
                            {subscription.company_name}
                          </span>
                          <span className='company-switch-badge opacity-0'>
                            Switch
                          </span>
                        </div>
                        {subDescription && (
                          <span
                            className='text-muted'
                            style={{ fontSize: 10, lineHeight: 1.18 }}
                          >
                            {subDescription}
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Second business CTA */}
          {start_another_business.enabled && (
            <div
              className='d-flex flex-column bg-action text-white cursor-pointer mb-4'
              style={{
                borderRadius: 13,
                padding: '15px 20px 20px',
              }}
              onClick={checkout}
            >
              <span
                className='fw-semibold'
                style={{
                  fontSize: 20,
                  letterSpacing: '-0.8px',
                  lineHeight: 1.18,
                }}
              >
                Have another business?
              </span>
              <span
                style={{
                  fontSize: 14,
                  letterSpacing: '-0.56px',
                  lineHeight: 1.28,
                  maxWidth: 252,
                }}
              >
                Sign up with our quick registration process and enjoy{' '}
                <strong>20% off</strong> your first year.
              </span>
              <Button
                theme='white'
                size='small'
                className='w-100'
                style={{ marginTop: 16 }}
              >
                Sign up
              </Button>
            </div>
          )}

          {/* Logout */}
          <Button
            theme='secondary'
            size='small'
            className='w-100'
            onClick={() => {
              router.replace('/')
              cognito.logout().finally(() => {
                logout({})
              })
            }}
            aria-label='Logout from your account'
          >
            Logout
          </Button>
        </div>
      )}
      {/* Crop Modal */}
      <ModalDefault
        isOpen={cropModalOpen}
        onDismiss={() => {
          setCropModalOpen(false)
          setCropImageSrc(null)
        }}
        toggle={() => setCropModalOpen(!cropModalOpen)}
      >
        <div className='position-relative' style={{ height: 300 }}>
          {cropImageSrc && (
            <Cropper
              image={cropImageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>
        <div className='d-flex align-items-center gap-3 pt-4 px-2'>
          <Icon name='zoom-out' fill='dark' width={16} height={16} />
          <Input
            type='range'
            inputClassName='form-range flex-grow-1'
            value={zoom}
            onChange={(e) =>
              setZoom(Number((e.target as HTMLInputElement).value))
            }
          />
          <Icon name='zoom-in' fill='dark' width={16} height={16} />
        </div>
        <div className='d-flex gap-5 py-4'>
          <div className='flex-fill'>
            <Button
              onClick={() => {
                setCropModalOpen(false)
                setCropImageSrc(null)
              }}
              type='button'
              className='btn-modal w-100'
              theme='secondary'
            >
              Cancel
            </Button>
          </div>
          <div className='flex-fill'>
            <Button
              onClick={handleCropConfirm}
              type='button'
              className='btn-modal w-100'
              theme='primary'
            >
              Confirm
            </Button>
          </div>
        </div>
      </ModalDefault>
    </div>
  )
}

export default SubscriptionSelect
